import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import Marquee from '../components/Marquee';
import MoreStories from '../components/MoreStories';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';
import { useLightbox } from '../hooks/useLightbox';

const galleryPhotos = [
  {
    src: '/vow-day-begins.jpg',
    caption: 'The Wedding Day Begins',
    location: 'Program, Parasols & Coral Roses',
  },
  {
    src: '/vow-bride-prep.jpg',
    caption: 'Monica Is Prepared',
    location: 'Hair, Makeup & Last Quiet Hour',
  },
  {
    src: '/vow-party-enters.jpg',
    caption: 'The Wedding Party Enters',
    location: 'A Procession in Mango & Rose',
  },
  {
    src: '/vow-first-kiss.jpg',
    caption: 'You May Kiss Your Bride',
    location: 'The Vow Sealed in Sepia Light',
  },
  {
    src: '/vow-recession.jpg',
    caption: 'Off to Their Reception',
    location: 'A Recessional of Parasols',
  },
];

const VowExchangeStory = () => {
  const [storyOpen, setStoryOpen] = useState(false);
  const storyRef = useRef(null);
  const lightbox = useLightbox();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleViewStory = () => {
    setStoryOpen(true);
    setTimeout(() => {
      storyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white">
      <SEO
        title="The Vow Exchange — Ryan & Monica's Wedding | Dion Magee Events"
        description="Beneath a sky of silk parasols, Ryan and Monica exchanged vows in Grand Prairie, TX. A coral-and-mango wedding story produced by Sir Dion Magee Events & Weddings."
        path="/portfolio/the-vow-exchange"
        image="/vow-first-kiss.jpg"
        type="article"
        article={{
          headline: "The Vow Exchange — Ryan & Monica's Wedding",
          datePublished: '2011-07-23',
          section: 'Wedding Stories',
        }}
      />
      {/* HERO */}
      <section className="relative h-[90vh] w-full overflow-hidden">
        <img
          src="/vow-first-kiss.jpg"
          alt="The Vow Exchange — Ryan & Monica"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-black/55" />

        <div className="absolute top-8 left-6 lg:top-12 lg:left-12 z-20">
          <Link
            to="/#portfolio"
            data-testid="back-to-portfolio"
            className="inline-flex items-center gap-3 text-white/80 hover:text-[#c9a96e] transition-colors text-[11px] tracking-[0.4em] uppercase"
          >
            <ArrowLeft size={16} />
            Back to Portfolio
          </Link>
        </div>

        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-20 lg:pb-28 px-6 text-center">
          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">
            A Wedding Story
          </p>
          <h1
            className="font-serif italic text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl leading-[1.05]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            The Vow Exchange
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mt-10" />
          <p className="text-white/70 text-sm tracking-[0.35em] uppercase mt-6">
            Ryan &nbsp;&amp;&nbsp; Monica &nbsp;&middot;&nbsp; Grand Prairie, TX &nbsp;&middot;&nbsp; July 23, 2011
          </p>
        </div>
      </section>

      {/* TOP GALLERY - 3 photos */}
      <section className="bg-[#0a0a0a] py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-[1500px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-5">The Chapters</p>
            <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl">
              A Day Bathed in Mango &amp; Rose
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-7" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            {galleryPhotos.slice(0, 3).map((p, i) => (
              <figure
                key={i}
                data-testid={`vow-photo-${i}`}
                onClick={() => lightbox.openAt(i)}
                className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter {String(i + 1).padStart(2, '0')}</p>
                  <h3
                    className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {p.caption}
                  </h3>
                  <p className="text-white/60 text-xs tracking-wider mt-2">{p.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* VIEW STORY CTA */}
      <section className="relative bg-[#0a0a0a] py-16 lg:py-24 px-6">
        <div className="pointer-events-none absolute -top-20 left-1/4 w-64 h-64 rounded-full bg-[#c9a96e]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 right-1/4 w-72 h-72 rounded-full bg-[#c9a96e]/10 blur-3xl" />

        <div className="relative max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>

          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">Two Vows, One Promise</p>
          <h3
            className="font-serif italic text-white text-3xl md:text-4xl lg:text-5xl mb-10 leading-snug"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Step into Ryan &amp; Monica&rsquo;s day &mdash; sealed beneath a sky of parasols.
          </h3>

          <button
            type="button"
            onClick={handleViewStory}
            data-testid="view-story-button"
            className="group relative inline-block px-14 sm:px-20 py-6 sm:py-7 border border-[#c9a96e] bg-transparent overflow-hidden transition-colors duration-500 hover:bg-[#c9a96e]/10"
          >
            <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#e8d5a8]" />
            <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#e8d5a8]" />
            <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#e8d5a8]" />
            <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#e8d5a8]" />
            <span
              className="font-serif italic text-[#e8d5a8] text-2xl sm:text-3xl tracking-[0.2em] relative z-10"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              View Story
            </span>
          </button>

          <div className="flex items-center justify-center gap-6 mt-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>
        </div>
      </section>

      {/* STORY NARRATIVE */}
      <section
        ref={storyRef}
        className={`bg-[#0a0a0a] px-6 lg:px-12 overflow-hidden transition-all duration-700 ease-out ${
          storyOpen ? 'max-h-[6000px] opacity-100 py-20 lg:py-28' : 'max-h-0 opacity-0 py-0'
        }`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.55em] uppercase mb-6">The Story</p>
            <h2
              className="font-serif italic text-white text-4xl md:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              A Promise Beneath the Parasols
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-8" />
          </div>

          <div
            className="space-y-7 text-white/80 text-lg leading-[1.9] font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="vow-story-text"
          >
            <p className="first-letter:font-serif first-letter:italic first-letter:text-[#c9a96e] first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]">
              On the morning of July 23, 2011, the Texas sun rose over Grand
              Prairie in a wash of mango and rose &mdash; the same colors that would
              later spill across every table, every bouquet, every silk parasol
              of Ryan and Monica&rsquo;s wedding. It was a day built around a
              single, quiet conviction: that some promises are too tender for
              ordinary words.
            </p>

            <p>
              The day began before the day did. A printed program rested
              beside a coral bouquet; a single pink parasol leaned against the
              wall, waiting for its moment. The chandeliers above whispered
              that something irreversible was about to happen, and the day &mdash;
              luminous, perfumed, full of held breaths &mdash; began.
            </p>

            <p>
              Monica was prepared in the gentle light of the bridal suite,
              the brush soft against her cheek, her bridesmaids gathered in a
              chorus of coral silk. Between each careful stroke she laughed
              quietly with the women she had chosen to stand beside her &mdash;
              her sisters in spirit, her witnesses for everything to come.
            </p>

            <p>
              Then, beneath the open Texas sky, the wedding party entered.
              Groomsmen and bridesmaids stepped forward two by two, the
              flower girl scattering petals like a small priest of joy. Roses
              and ranunculus burned brightly in every hand &mdash; orange, rose,
              coral, mango &mdash; carried like little suns down the aisle.
            </p>

            <p>
              And then the moment that gave the day its name. Ryan and Monica
              stood at the altar in sepia light, her tiara catching the sun,
              his hand trembling just slightly as he lifted her veil. The
              officiant&rsquo;s voice grew gentle, almost reverent. &ldquo;You may
              kiss your bride.&rdquo; And they did &mdash; her arms around his neck,
              his hand at the small of her back, the world holding still.
            </p>

            <p>
              When they turned to face their guests, an aisle of silk parasols
              had bloomed before them &mdash; held aloft by everyone they loved,
              like a canopy of welcome and benediction. Hand in hand, Mr. and
              Mrs. Ryan and Monica walked into the rest of their lives, their
              reception waiting beyond the doors, the day only just beginning
              to keep its promise.
            </p>

            <p className="text-center text-[#c9a96e] italic pt-6">
              &mdash; And from that day forward, beneath every sky, they would carry their parasols of joy.
            </p>
          </div>

          {/* BOTTOM GALLERY - 2 more photos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-20 max-w-4xl mx-auto">
            {galleryPhotos.slice(3).map((p, i) => (
              <figure
                key={i + 3}
                data-testid={`vow-photo-${i + 3}`}
                onClick={() => lightbox.openAt(i + 3)}
                className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter {String(i + 4).padStart(2, '0')}</p>
                  <h3
                    className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {p.caption}
                  </h3>
                  <p className="text-white/60 text-xs tracking-wider mt-2">{p.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="text-center mt-20">
            <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-8" />
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">An Original Production by</p>
            <p
              className="font-serif italic text-white text-3xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Dion Magee Events &amp; Weddings
            </p>
            <Link
              to="/#contact"
              data-testid="story-cta-enquire"
              className="inline-block mt-10 px-10 py-4 border border-[#c9a96e] text-[#e8d5a8] text-[11px] tracking-[0.4em] uppercase hover:bg-[#c9a96e] hover:text-black transition-colors duration-500"
            >
              Begin Your Own Story
            </Link>
          </div>
        </div>
      </section>

      <MoreStories currentSlug="the-vow-exchange" />
      <Marquee />
      <Footer />
      <Lightbox
        open={lightbox.open}
        onClose={lightbox.close}
        photos={galleryPhotos}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
      />
    </div>
  );
};

export default VowExchangeStory;
